// Everything between the API's rows and the Reports markup.
//
// This file exists because there is no DOM test harness in this repo and none is
// being added. Every test under test/web/ works by being a pure module beside the
// component that uses it (`queue-query.js`, `sla-state.js`, `newContactForm.js`),
// so all the arithmetic and all the English live here, where
// test/web/report-shape.test.js can reach them without rendering anything.
//
// The design (docs/REPORTS-TAB-DESIGN.md §6) places this at
// web/src/components/reports/. It is here instead, beside its page, because
// web/src/pages/reports/ is the directory this task owns and six agents are writing
// this tree concurrently. Same pattern, one directory over.
//
// THE ONE RULE: a report that cannot be computed honestly must not ship. In this
// file that shows up as a distinction the rest of the screen depends on —
// **zero and unrecorded are not the same value**. A day with no tickets is 0. A day
// before this system recorded anything is `unrecorded`, and it renders as an em dash
// with a title, never as a bar of height nothing. Drawing the second as the first is
// the exact lie the whole design exists to prevent.

/** The three channels, in the order they are stacked in a bar and listed in a table. */
export const CHANNELS = ['phone', 'email', 'api'];

/** Percentiles below this many ANSWERED clocks in a group are not quoted. */
export const PERCENTILE_FLOOR = 20;

/** `true` when a median may be printed as a number rather than as a sentence. */
export function quotable(answered) {
  return Number.isFinite(answered) && answered >= PERCENTILE_FLOOR;
}

const MS_PER_DAY = 86400000;

/**
 * 'YYYY-MM-DD' to the UTC-midnight epoch of that wall-clock date.
 *
 * ALL day arithmetic in this file happens in Date.UTC space, and that is not a
 * style choice. `new Date('2026-10-30')` followed by `setDate(d + 1)` walks local
 * time, so on the night a DST offset changes it either repeats a day or skips one,
 * and the resulting ISO string lands on the wrong date. UTC has no such nights.
 * The report's LOCAL calendar day was already decided by SQL Server, in the report's
 * timezone, and arrives here as text — so nothing on this side has to know a zone.
 */
function dayToMs(day) {
  const year = Number(day.slice(0, 4));
  const month = Number(day.slice(5, 7));
  const dayOfMonth = Number(day.slice(8, 10));
  return Date.UTC(year, month - 1, dayOfMonth);
}

function msToDay(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}

/** Every calendar day from `from` to `to`, both inclusive, as 'YYYY-MM-DD'. */
export function eachDay(from, to) {
  const end = dayToMs(to);
  const days = [];
  for (let ms = dayToMs(from); ms <= end; ms += MS_PER_DAY) days.push(msToDay(ms));
  return days;
}

/**
 * Sparse API rows to one entry per calendar day in the window.
 *
 * @param {Array<{metric: string, day: string, channel: string, n: number}>} rows
 * @param {string} from          inclusive local date, 'YYYY-MM-DD'
 * @param {string} to            inclusive local date, 'YYYY-MM-DD'
 * @param {string|null} dataFromDay  meta.data_from_day — the LOCAL day this system's
 *   records begin. Days before it are `unrecorded`. Null means the database holds no
 *   tickets at all, in which case nothing can be claimed about any day and every one
 *   of them is unrecorded.
 * @returns {Array<{day: string, unrecorded: boolean, rows: Array}>}
 */
export function fillDays(rows, from, to, dataFromDay = null) {
  const byDay = new Map();
  for (const row of rows ?? []) {
    if (!byDay.has(row.day)) byDay.set(row.day, []);
    byDay.get(row.day).push(row);
  }
  // A lexicographic compare is a chronological compare for zero-padded ISO dates,
  // which is most of why the day travels as text.
  return eachDay(from, to).map((day) => ({
    day,
    unrecorded: dataFromDay === null || day < dataFromDay,
    rows: byDay.get(day) ?? [],
  }));
}

/**
 * The long `{metric, day, channel, n}` rows to one record per day.
 *
 * An unrecorded day carries `null` counts rather than zeros, so a component cannot
 * accidentally render it as a real observation of nothing — the types make the two
 * cases impossible to confuse downstream.
 */
export function pivotVolume(days) {
  return days.map((entry) => {
    if (entry.unrecorded) {
      return {
        day: entry.day,
        unrecorded: true,
        created: { phone: null, email: null, api: null, total: null },
        solved: null,
      };
    }
    const created = { phone: 0, email: 0, api: 0, total: 0 };
    let solved = 0;
    for (const row of entry.rows) {
      const n = row.n ?? 0;
      if (row.metric === 'created') {
        if (row.channel in created) created[row.channel] += n;
        created.total += n;
      } else if (row.metric === 'solved') {
        solved += n;
      }
    }
    return { day: entry.day, unrecorded: false, created, solved };
  });
}

/**
 * The stat tiles above the day table.
 *
 * `days` counts only RECORDED days, so "58/day over 7 days" never divides real
 * tickets by days the system was not running — which would quietly understate the
 * desk by however far back the window reaches.
 */
export function volumeTotals(pivoted) {
  const recorded = pivoted.filter((entry) => !entry.unrecorded);
  const created = recorded.reduce((sum, entry) => sum + entry.created.total, 0);
  const solved = recorded.reduce((sum, entry) => sum + entry.solved, 0);
  let busiest = null;
  for (const entry of recorded) {
    if (busiest === null || entry.created.total > busiest.created.total) busiest = entry;
  }
  return {
    created,
    solved,
    net: created - solved,
    recordedDays: recorded.length,
    perDay: recorded.length > 0 ? created / recorded.length : null,
    busiest,
  };
}

/** The largest single-day created total in the window; the bar scale. 0 if none. */
export function maxDailyTotal(pivoted) {
  return pivoted.reduce(
    (max, entry) => (entry.unrecorded ? max : Math.max(max, entry.created.total)),
    0,
  );
}

/**
 * Segment widths, as percentages of the whole track.
 *
 * Scaled against `max` — the maximum daily total in the window — NOT against the
 * row's own total. A row-relative bar draws a 1-ticket Sunday and a 69-ticket Monday
 * the same width, which is the classic dashboard lie: bar length has to mean volume
 * and segment ratio has to mean mix.
 *
 * Two properties this must hold, both of which are visible bugs when they break:
 *   - `max` of 0 returns zeros, never NaN. `width: NaN%` is an invalid declaration,
 *     so the browser drops it and the segment renders at its content width instead
 *     of collapsing.
 *   - the segments never sum above 100 after rounding. A stacked flex track that
 *     overflows wraps and silently doubles the row height.
 * Rounding is therefore DOWN, never to-nearest: to-nearest can push the sum past
 * 100, and a fraction of a percent of visual length is worth nothing next to that.
 */
export function barWidths(values, max) {
  const list = (values ?? []).map((v) => (Number.isFinite(v) && v > 0 ? v : 0));
  const scale = Number.isFinite(max) && max > 0 ? max : 0;
  if (scale === 0) return list.map(() => 0);

  const floored = list.map((v) => Math.floor((v / scale) * 10000) / 100);
  const total = floored.reduce((sum, p) => sum + p, 0);
  if (total <= 100) return floored;
  // Only reachable if a caller scales values against a max smaller than they sum
  // to. Normalising keeps the track intact rather than trusting the caller.
  return floored.map((p) => Math.floor((p / total) * 10000) / 100);
}

const EMPTY_GROUP = {
  tickets: 0,
  clocks: 0,
  no_clock: 0,
  answered_in_target: 0,
  answered_late: 0,
  unanswered_overdue: 0,
  unanswered_in_target: 0,
  target_min_low: null,
  target_min_high: null,
  p50_sec: null,
  p90_sec: null,
};

/**
 * Always exactly two groups, non-phone first.
 *
 * The API returns zero rows for an empty window rather than two zeroed ones, so that
 * "no data" and "all zeros" stay distinguishable on the wire. The screen renders both
 * as zeros — a missing card reads as a broken page — so the synthesis happens here,
 * where it is testable, rather than in the route.
 *
 * Non-phone is first because phone's numbers are an artefact: a phone clock is
 * satisfied at creation by a CASE expression in `startFirstResponseClock`, so its
 * median is 0 by construction. Putting the real measurement first, and phone second
 * with a note attached, is the whole reason this report has two cards instead of one.
 */
export function fillChannelGroups(groups) {
  const byGroup = new Map((groups ?? []).map((group) => [group.channel_group, group]));
  return ['non_phone', 'phone'].map((name) => ({
    channel_group: name,
    ...EMPTY_GROUP,
    ...(byGroup.get(name) ?? {}),
  }));
}

/** The four clock states, in the fixed order the state bar stacks them. */
export function clockStates(group) {
  return [
    { key: 'answered_in_target', label: 'Answered in target', n: group.answered_in_target ?? 0 },
    { key: 'answered_late', label: 'Answered late', n: group.answered_late ?? 0 },
    { key: 'unanswered_overdue', label: 'Waiting, overdue', n: group.unanswered_overdue ?? 0 },
    { key: 'unanswered_in_target', label: 'Waiting, in target', n: group.unanswered_in_target ?? 0 },
  ];
}

/** Answered clocks in a group — the denominator the percentile floor is judged on. */
export function answeredCount(group) {
  return (group.answered_in_target ?? 0) + (group.answered_late ?? 0);
}

export const CHANNEL_GROUP_LABELS = {
  non_phone: 'Email & API',
  phone: 'Phone',
};

/**
 * Seconds to something a person reads.
 *
 * `formatDuration(0)` is '0s' and deliberately NOT an em dash. A phone clock
 * satisfied at t=0 is a REAL zero — an agent was already on the call — and rendering
 * it as "no data" would hide the very artefact the phone/non-phone split exists to
 * expose. The em dash is reserved for null, which means genuinely nothing to show.
 */
export function formatDuration(seconds) {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '-';
  const total = Math.round(seconds);
  if (total < 0) return '-';
  if (total < 60) return `${total}s`;

  const minutes = Math.floor(total / 60);
  if (minutes < 60) {
    const rest = total % 60;
    return rest === 0 ? `${minutes}m` : `${minutes}m ${rest}s`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rest = minutes % 60;
    return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
  }
  const days = Math.floor(hours / 24);
  const rest = hours % 24;
  return rest === 0 ? `${days}d` : `${days}d ${rest}h`;
}

/** Age buckets, in order, with the English the server deliberately does not send. */
export const AGE_BUCKETS = [
  { bucket: 'h0_24', label: 'Under 1 day' },
  { bucket: 'd1_4', label: '1–4 days' },
  { bucket: 'd4_14', label: '4–14 days' },
  { bucket: 'd14_30', label: '14–30 days' },
  { bucket: 'd30_plus', label: 'Over 30 days' },
];

export const UNASSIGNED_LABEL = 'Unassigned';

/**
 * The backlog's three slices, from the one statement that produced them.
 *
 * ZERO-FILLING IS LEGITIMATE HERE, and it is worth being explicit about why, because
 * the volume report refuses to do the same thing. Backlog is a COMPLETE CENSUS at one
 * instant: every unsolved ticket is counted, so a bucket the SQL did not return
 * matched no rows and its count is genuinely 0. In the volume report an absent day
 * might instead mean "before this system was recording", which is not zero and must
 * never be drawn as zero. Same operation, opposite honesty — the difference is
 * whether the denominator is known.
 *
 * MEASURED: with every open ticket assigned, the statement returns no NULL-bucket
 * assignee row at all. Unassigned is therefore synthesised at 0 and pinned to the
 * top regardless of count, because it is the row that gets acted on.
 */
export function shapeBacklog(rows) {
  const all = rows ?? [];
  const of = (dimension) => all.filter((row) => row.dimension === dimension);

  const ageByBucket = new Map(of('age').map((row) => [row.bucket, row]));
  const age = AGE_BUCKETS.map(({ bucket, label }) => ({
    bucket,
    label,
    n: ageByBucket.get(bucket)?.n ?? 0,
    oldest_created_at: ageByBucket.get(bucket)?.oldest_created_at ?? null,
  }));

  const assigneeRows = of('assignee');
  const unassigned = assigneeRows.find((row) => row.bucket === null) ?? {
    bucket: null,
    label: null,
    n: 0,
    oldest_created_at: null,
  };
  const assignee = [
    { ...unassigned, label: UNASSIGNED_LABEL },
    ...assigneeRows
      .filter((row) => row.bucket !== null)
      .slice()
      .sort((a, b) => b.n - a.n),
  ];

  const status = of('status').slice().sort((a, b) => b.n - a.n);

  // The total is taken from ONE dimension rather than summed across all three: each
  // slice is a partition of the same row set, so summing them would treble it.
  const total = age.reduce((sum, row) => sum + row.n, 0);

  return { age, assignee, status, total };
}

/**
 * A note code to the sentence the screen prints.
 *
 * The codes are emitted by the route that ran the SQL and the English lives here, so
 * a caveat cannot drift away from the number it qualifies — which is what happens
 * when the sentence is hardcoded in a component and the query changes underneath it.
 *
 * An unknown code returns null rather than a placeholder: a note nobody wrote English
 * for should be silent, not render as "[object Object]" over a leadership dashboard.
 */
export function noteText(note) {
  if (!note || typeof note.code !== 'string') return null;
  switch (note.code) {
    case 'window_precedes_data':
      return 'This window starts before the first ticket on record, so the earliest '
        + 'days show no data rather than zero.';
    case 'solved_is_current_state':
      return 'Solved counts reflect each ticket’s current state. Reopening a ticket '
        + 'clears its resolved date, so it leaves this series retrospectively.';
    case 'phone_auto_satisfied':
      return 'Phone tickets are satisfied at creation, by design. An agent was '
        + 'already on the call. Their reply time is 0 by construction and is not a '
        + 'measurement.';
    case 'clocks_missing':
      return note.n === 1
        ? '1 ticket has no first-reply clock at all, so it is counted as a ticket but '
          + 'cannot be counted as answered or late.'
        : `${note.n} tickets have no first-reply clock at all, so they are counted as `
          + 'tickets but cannot be counted as answered or late.';
    case 'sample_too_small':
      return `${CHANNEL_GROUP_LABELS[note.group] ?? note.group}: `
        + `${note.n} answered clock${note.n === 1 ? '' : 's'} is below the `
        + `${note.floor} needed to quote a median.`;
    case 'target_varies':
      return `${CHANNEL_GROUP_LABELS[note.group] ?? note.group}: this window spans a `
        + `target change (${note.low}–${note.high} minutes), so the states below are `
        + 'measured against more than one promise.';
    case 'age_is_since_created':
      return 'Age is measured from when a ticket was created, not from when anyone last '
        + 'touched it.';
    default:
      return null;
  }
}

/** Every note that has English, in the order the API emitted them. */
export function noteSentences(notes) {
  return (notes ?? []).map(noteText).filter((text) => text !== null);
}
